const API_BASE = "https://openapi.nocodebackend.com";
const INSTANCE = "54678_smartlink_tags";
const TOKEN = import.meta.env.VITE_NOCODE_API_KEY;

// Helper to generate short readable ID
const generateShortId = () => Math.random().toString(36).substring(2, 8).toUpperCase();

// Helper to generate numeric edit code
const generateEditCode = () => Math.floor(100000 + Math.random() * 900000).toString();

const getHeaders = (hasBody = true) => {
  const headers = {
    'Authorization': `Bearer ${TOKEN}`
  };
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

export const db = {
  // --- TAGS OPERATIONS ---
  getAll: async () => {
    try {
      const url = `${API_BASE}/read/tags?Instance=${INSTANCE}&sort=id&order=desc`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(false)
      });
      const json = await response.json();
      return Array.isArray(json.data) ? json.data : [];
    } catch (error) {
      console.error("DB Error (getAll):", error);
      return [];
    }
  },

  findByTagId: async (tagCode) => {
    try {
      // Using search to find by tag_code
      const response = await fetch(`${API_BASE}/search/tags?Instance=${INSTANCE}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ tag_code: tagCode })
      });
      const json = await response.json();
      return (json.data && json.data.length > 0) ? json.data[0] : null;
    } catch (error) {
      console.error("DB Error (findByTagId):", error);
      return null;
    }
  },

  findByEditCode: async (code) => {
    try {
      const response = await fetch(`${API_BASE}/search/tags?Instance=${INSTANCE}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ owner_phone: code }) // Mapping owner_phone to edit_code
      });
      const json = await response.json();
      return (json.data && json.data.length > 0) ? json.data[0] : null;
    } catch (error) {
      console.error("DB Error (findByEditCode):", error);
      return null;
    }
  },

  create: async (tagId = null) => {
    const finalTagId = tagId || generateShortId();
    const editCode = generateEditCode();

    // Default structure for new tag
    const newRecord = {
      tag_code: finalTagId,
      redirect_url: "",
      link_type: "direct", // Default
      is_active: 0,
      scan_count: 0,
      owner_phone: editCode, // Using this as the secret edit code
      company_name: "",
      logo_url: "",
      bio: "",
      negative_review_filter_enabled: 0,
      google_review_link: "",
      dummy_page_message: ""
    };

    try {
      const response = await fetch(`${API_BASE}/create/tags?Instance=${INSTANCE}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify(newRecord)
      });
      const json = await response.json();
      if (response.ok) {
        return { id: json.id, ...newRecord };
      } else {
        console.error("Create failed:", json);
        return null;
      }
    } catch (error) {
      console.error("DB Error (create):", error);
      return null;
    }
  },

  update: async (id, updates) => {
    if (!id) return null;
    try {
      const response = await fetch(`${API_BASE}/update/tags/${id}?Instance=${INSTANCE}`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error(`Update failed: ${response.status}`);
      return true;
    } catch (error) {
      console.error("DB Error (update):", error);
      throw error;
    }
  },

  delete: async (id) => {
    if (!id) return false;
    try {
      const response = await fetch(`${API_BASE}/delete/tags/${id}?Instance=${INSTANCE}`, {
        method: 'DELETE',
        headers: getHeaders(false)
      });
      return response.ok;
    } catch (error) {
      console.error("DB Error (delete):", error);
      return false;
    }
  },

  incrementScan: async (tag) => {
    if (!tag || !tag.id) return;
    const newCount = (parseInt(tag.scan_count) || 0) + 1;
    await db.update(tag.id, { scan_count: newCount });
  },

  // --- PROFILE LINKS OPERATIONS ---
  getProfileLinks: async (tagCode) => {
    try {
      const response = await fetch(`${API_BASE}/search/profile_links?Instance=${INSTANCE}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ tag_code: tagCode })
      });
      const json = await response.json();
      // Sort by position
      return Array.isArray(json.data) ? json.data.sort((a, b) => (a.position || 0) - (b.position || 0)) : [];
    } catch (error) {
      console.error("DB Error (getProfileLinks):", error);
      return [];
    }
  },

  // Syncs the UI state of links with the Backend (Create, Update, Delete)
  syncProfileLinks: async (tagCode, newLinks) => {
    try {
      // 1. Get existing links
      const existingLinks = await db.getProfileLinks(tagCode);
      
      // 2. Identify links to delete (exist in DB but not in new list)
      // We match by ID if available. 
      // Strategy: 
      // - If new link has an 'id', it's an update.
      // - If new link has no 'id', it's a create.
      // - Any existing link 'id' NOT in the new list is a delete.
      const newIds = new Set(newLinks.map(l => l.id).filter(Boolean));
      const toDelete = existingLinks.filter(l => !newIds.has(l.id));

      // Execute Deletes
      for (const link of toDelete) {
        await fetch(`${API_BASE}/delete/profile_links/${link.id}?Instance=${INSTANCE}`, {
          method: 'DELETE',
          headers: getHeaders(false)
        });
      }

      // Execute Creates and Updates
      for (let i = 0; i < newLinks.length; i++) {
        const link = newLinks[i];
        const payload = {
          tag_code: tagCode,
          label: link.label,
          url: link.url,
          position: i
        };

        if (link.id) {
          // Update
          await fetch(`${API_BASE}/update/profile_links/${link.id}?Instance=${INSTANCE}`, {
            method: 'PUT',
            headers: getHeaders(true),
            body: JSON.stringify(payload)
          });
        } else {
          // Create (only if it has content)
          if (link.label || link.url) {
            await fetch(`${API_BASE}/create/profile_links?Instance=${INSTANCE}`, {
              method: 'POST',
              headers: getHeaders(true),
              body: JSON.stringify(payload)
            });
          }
        }
      }
      return true;
    } catch (error) {
      console.error("DB Error (syncProfileLinks):", error);
      throw error;
    }
  },

  // --- NEGATIVE REVIEWS OPERATIONS ---
  createNegativeReview: async (data) => {
    try {
      const response = await fetch(`${API_BASE}/create/negative_reviews?Instance=${INSTANCE}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
          ...data,
          created_at: new Date().toISOString()
        })
      });
      return response.ok;
    } catch (error) {
      console.error("DB Error (createNegativeReview):", error);
      return false;
    }
  },

  getAllNegativeReviews: async () => {
    try {
      const url = `${API_BASE}/read/negative_reviews?Instance=${INSTANCE}&sort=id&order=desc`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(false)
      });
      const json = await response.json();
      return Array.isArray(json.data) ? json.data : [];
    } catch (error) {
      console.error("DB Error (getAllNegativeReviews):", error);
      return [];
    }
  },

  deleteNegativeReview: async (id) => {
    if (!id) return false;
    try {
      const response = await fetch(`${API_BASE}/delete/negative_reviews/${id}?Instance=${INSTANCE}`, {
        method: 'DELETE',
        headers: getHeaders(false)
      });
      return response.ok;
    } catch (error) {
      console.error("DB Error (deleteNegativeReview):", error);
      return false;
    }
  }
};